<div class="graph-test-page">
  <header class="graph-test-header">
    <h1>Microsoft Graph user test</h1>
    <p class="hint">
      GET then PATCH
      <code>graph.microsoft.com/v1.0/users/&#123;USER_ID&#125;</code>
      with <code>mail</code> and
      <code>identities</code> (federated <code>issuer: mail</code>, plus unchanged
      <code>userPrincipalName</code>).
    </p>
  </header>

  <div class="actions">
    <button type="button" class="primary" [disabled]="loading || patching" (click)="loadAndFetchGraph()">
      {{ loading ? 'Loading…' : 'Refresh Graph GET' }}
    </button>
    <a routerLink="/login" class="link">Back to sign-in</a>
  </div>

  <p class="error" *ngIf="error">{{ error }}</p>

  <section class="panel" *ngIf="userId">
    <h2>Session</h2>
    <dl>
      <dt>USER_ID (Entra object id)</dt>
      <dd><code>{{ userId }}</code></dd>
      <dt>Accounts user id</dt>
      <dd><code>{{ accountsUserId || '—' }}</code></dd>
      <dt>Email (JWT / Accounts)</dt>
      <dd>{{ email || '—' }}</dd>
      <dt>Graph GET URL</dt>
      <dd class="break"><code>{{ graphRequestUrl }}</code></dd>
      <dt>GRAPH_ACCESS_TOKEN</dt>
      <dd class="break token"><code>{{ graphAccessToken }}</code></dd>
    </dl>
  </section>

  <section class="panel patch-panel" *ngIf="userId && graphAccessToken">
    <h2>PATCH user (mail + identities)</h2>
    <p class="hint patch-hint">
      Sends <code>mail</code>,
      <code>identities</code> (federated <code>issuer: "mail"</code>, unchanged
      <code>userPrincipalName</code>), and optionally
      <code>proxyAddresses</code> (new <code>SMTP:</code> primary, remove old alias).
      For Exchange Online mailboxes, Graph may not fully sync aliases — use Exchange cleanup below.
    </p>
    <label class="field-label" for="oldEmail">Old email (alias to remove)</label>
    <input
      id="oldEmail"
      class="email-input"
      type="email"
      [(ngModel)]="oldEmail"
      (ngModelChange)="previewPatchPayload()"
      placeholder="previous@example.com"
      autocomplete="off"
    />
    <label class="field-label" for="newEmail">New email</label>
    <input
      id="newEmail"
      class="email-input"
      type="email"
      [(ngModel)]="newEmail"
      (ngModelChange)="previewPatchPayload()"
      placeholder="new@example.com"
      autocomplete="off"
    />
    <label class="checkbox-row">
      <input type="checkbox" [(ngModel)]="includeProxyAddresses" (ngModelChange)="previewPatchPayload()" />
      Include <code>proxyAddresses</code> in PATCH (Graph)
    </label>
    <div class="patch-actions">
      <button type="button" class="secondary" [disabled]="!newEmail.trim() || patching" (click)="previewPatchPayload()">
        Preview PATCH body
      </button>
      <button type="button" class="primary" [disabled]="!canPatchEmail" (click)="patchUserEmail()">
        {{ patching ? 'Patching…' : 'PATCH user identities' }}
      </button>
      <button
        type="button"
        class="secondary"
        [disabled]="!canPatchEmail"
        (click)="patchProxyAddressesOnly()"
      >
        PATCH proxyAddresses only
      </button>
    </div>
    <p class="error" *ngIf="patchError">{{ patchError }}</p>
    <div *ngIf="patchRequestJson">
      <h3 class="subhead">PATCH request body</h3>
      <pre>{{ patchRequestJson }}</pre>
    </div>
    <div *ngIf="patchResponseJson || patchStatus !== null">
      <h3 class="subhead">
        PATCH response
        <span *ngIf="patchStatus !== null">(HTTP {{ patchStatus }})</span>
      </h3>
      <pre *ngIf="patchResponseJson">{{ patchResponseJson }}</pre>
    </div>
  </section>

  <section class="panel patch-panel" *ngIf="userId && graphAccessToken">
    <h2>Exchange cleanup (Automation webhook)</h2>
    <p class="hint patch-hint">
      Mirrors your Node sample: wait ~10s, then POST
      <code>{{ '{' }} user, oldEmail, newEmail {{ '}' }}</code>
      to <code>EXCHANGE_EMAIL_CLEANUP_WEBHOOK_URL</code> on Accounts API (runbook
      <code>Set-Mailbox</code> / remove old SMTP).
      Webhook configured:
      <strong>{{ exchangeWebhookConfigured ? 'yes' : 'no' }}</strong>
    </p>
    <div class="patch-actions">
      <button
        type="button"
        class="secondary"
        [disabled]="exchangeLoading || patching || !exchangeWebhookConfigured"
        (click)="triggerExchangeCleanup()"
      >
        {{ exchangeLoading ? 'Calling webhook…' : 'Exchange cleanup only' }}
      </button>
      <button
        type="button"
        class="primary"
        [disabled]="!canPatchEmail || exchangeLoading || patching || !exchangeWebhookConfigured"
        (click)="patchThenExchangeCleanup()"
      >
        {{ patching || exchangeLoading ? 'Working…' : 'PATCH Graph + Exchange cleanup' }}
      </button>
    </div>
    <p class="error" *ngIf="exchangeError">{{ exchangeError }}</p>
    <div *ngIf="exchangeResponseJson">
      <h3 class="subhead">Webhook response</h3>
      <pre>{{ exchangeResponseJson }}</pre>
    </div>
  </section>

  <section class="panel lookup-panel" *ngIf="graphAccessToken">
    <h2>GET user by id (mail + identities)</h2>
    <p class="hint patch-hint">
      <code>GET graph.microsoft.com/v1.0/users/&#123;id&#125;?$select={{ fullGraphSelect }}</code>
    </p>
    <label class="field-label" for="lookupUserId">User object id</label>
    <input
      id="lookupUserId"
      class="email-input lookup-id-input"
      type="text"
      [(ngModel)]="lookupUserId"
      placeholder="650101cd-a897-49f6-91a3-de01f6dbae46"
      autocomplete="off"
      spellcheck="false"
    />
    <div class="patch-actions">
      <button
        type="button"
        class="primary"
        [disabled]="lookupLoading || !lookupUserId.trim()"
        (click)="fetchLookupUserById()"
      >
        {{ lookupLoading ? 'Loading…' : 'GET full user profile' }}
      </button>
    </div>
    <p class="error" *ngIf="lookupError">{{ lookupError }}</p>
    <dl *ngIf="lookupRequestUrl">
      <dt>Request URL</dt>
      <dd class="break"><code>{{ lookupRequestUrl }}</code></dd>
    </dl>
    <div *ngIf="lookupResponseJson">
      <h3 class="subhead">
        Response
        <span *ngIf="lookupStatus !== null">(HTTP {{ lookupStatus }})</span>
      </h3>
      <pre>{{ lookupResponseJson }}</pre>
    </div>
  </section>

  <section class="panel" *ngIf="graphResponseJson">
    <h2>Graph GET response <span *ngIf="graphStatus !== null">(HTTP {{ graphStatus }})</span></h2>
    <pre>{{ graphResponseJson }}</pre>
  </section>
</div>
